/**
 * Default sort priority and pinned visibility for table filter pills.
 * See docs/table-filters-inventory.md.
 */

const ENTITY_PRESETS = new Set([
    "customer",
    "provider",
    "catalog",
    "providercatalog",
    "vendor",
    "billed",
    "recipient",
    "receipt_owner",
    "initiator",
    "stock",
    "manufacturer",
    "product",
    "establishment",
    "deliverysite",
    "invoice_provider",
    "stock_inventory_owner",
    "stockproduct_list_warehouse",
    "stockproduct_list_owner",
]);

const STATUS_PRESETS = new Set([
    "customerorder_status",
    "providerorder_status",
    "customerinvoice_status",
    "providerinvoice_status",
    "commission_status",
    "allocation_status",
    "shipment_status",
    "delegatedshipment_status",
    "receipt_status",
    "delegatedreceipt_status",
    "invoice_paid",
    "payment_status",
    "receipt_placed",
]);

const DEFER_PIN_PRESETS = new Set([
    "date",
    "tags",
    "assigned_to",
    "billingaccount",
    "invoice_date",
    "due_date",
    "completed_date",
    "validated_date",
    "paid_date",
    "delivery_date",
    "receipt_date",
    "shipment_date",
    "invoice_credit",
    "payment_method",
    "include_archived",
    "include_canceled",
    "include_archived_products",
    "exclude_readonly_orders",
    "exclude_readonly_shipments",
    "exclude_readonly_receipts",
    "exclude_entries_with_no_stock",
    "exclude_entries_with_no_provider_stock",
    "exclude_products_with_no_stock",
    "missing_purchase_price",
    "low_stock",
    "connected",
    "country",
    "city",
    "postal_code",
    "persona",
    "catalogentry_category",
    "catalogentry_type",
    "catalogentry_trigger",
    "catalogentry_recurring",
    "movement_type",
    "delegated",
    "paid",
    "shop_providers",
    "shop_producttypes",
]);

const ALWAYS_PIN_PRESETS = new Set(["producttype", "invoice_type"]);

function isSearchOrDynamic(conf) {
    return conf?.type === "search" || conf?.type === "dynamicfilters";
}

function resolvePreset(conf) {
    return conf?.preset || null;
}

function isEntityFilter(conf) {
    const preset = resolvePreset(conf);
    if (preset && ENTITY_PRESETS.has(preset)) {
        return true;
    }
    const field = conf?.field;
    if (field === "catalog" || field === "customer" || field === "provider") {
        return true;
    }
    return false;
}

function isStatusFilter(conf) {
    const preset = resolvePreset(conf);
    if (preset && STATUS_PRESETS.has(preset)) {
        return true;
    }
    if (conf?.field === "status" && conf?.type === "select") {
        return true;
    }
    if (conf?.field === "active" && conf?.type === "select") {
        return true;
    }
    return false;
}

function isProductMetadataField(conf) {
    const field = conf?.field || "";
    return field.startsWith("metadata__");
}

function isCustomerTypeFilter(conf) {
    return conf?.field === "type" && conf?.type === "select" && conf?.field !== "producttype";
}

function isOrderTypeFilter(conf) {
    return conf?.field === "order_type";
}

/**
 * Lower value = earlier in the filter bar.
 */
export function getFilterSortPriority(conf) {
    if (isSearchOrDynamic(conf)) {
        return 0;
    }
    if (isEntityFilter(conf)) {
        return 10;
    }
    if (isCustomerTypeFilter(conf)) {
        return 15;
    }
    if (isStatusFilter(conf)) {
        return 25;
    }
    if (isOrderTypeFilter(conf)) {
        return 28;
    }
    if (resolvePreset(conf) === "producttype" || conf?.field === "producttype") {
        return 50;
    }
    if (isProductMetadataField(conf)) {
        return 55;
    }
    if (resolvePreset(conf) === "invoice_type") {
        return 60;
    }

    const preset = resolvePreset(conf);
    if (preset && DEFER_PIN_PRESETS.has(preset)) {
        if (["invoice_date", "due_date", "completed_date", "validated_date", "paid_date", "delivery_date", "receipt_date", "shipment_date", "date"].includes(preset)) {
            return 200;
        }
        if (preset === "tags") {
            return 220;
        }
        if (preset === "assigned_to") {
            return 230;
        }
        if (preset === "billingaccount") {
            return 240;
        }
        return 260;
    }

    if (conf?.type === "daterange" || conf?.type === "numberrange") {
        return 200;
    }
    if (conf?.type === "slider") {
        return 210;
    }
    if (conf?.type === "boolean") {
        return 260;
    }
    return 150;
}

function countPillCandidates(confs) {
    return confs.filter((conf) => !isSearchOrDynamic(conf)).length;
}

/**
 * Whether a filter should stay visible as a pill (pinned) when the panel is open.
 */
export function shouldPinFilter(conf, allConfs) {
    if (isSearchOrDynamic(conf)) {
        return false;
    }
    if (conf.pinned === true || conf.force_display === true) {
        return true;
    }
    if (conf.pinned === false) {
        return false;
    }

    const pillCount = countPillCandidates(allConfs);
    if (pillCount <= 4) {
        return true;
    }

    const preset = resolvePreset(conf);
    if (preset && ALWAYS_PIN_PRESETS.has(preset)) {
        return true;
    }
    if (isEntityFilter(conf) || isStatusFilter(conf) || isCustomerTypeFilter(conf) || isOrderTypeFilter(conf)) {
        return true;
    }
    if (isProductMetadataField(conf)) {
        return true;
    }
    if (preset && DEFER_PIN_PRESETS.has(preset)) {
        return false;
    }
    if (conf?.type === "daterange" || conf?.type === "numberrange" || conf?.type === "boolean") {
        return false;
    }
    return false;
}

/**
 * Reorder filters and set `pinned` on each config (mutates copies).
 */
export function applyTableFiltersLayout(fullFiltersConf) {
    if (!fullFiltersConf?.length) {
        return [];
    }

    const confs = fullFiltersConf.map((conf) => ({ ...conf }));
    const search = confs.filter((conf) => conf.type === "search");
    const dynamic = confs.filter((conf) => conf.type === "dynamicfilters");
    const rest = confs.filter((conf) => !isSearchOrDynamic(conf));

    rest.sort((a, b) => {
        const diff = getFilterSortPriority(a) - getFilterSortPriority(b);
        if (diff !== 0) {
            return diff;
        }
        return String(a.label || a.field || "").localeCompare(String(b.label || b.field || ""));
    });

    const ordered = [...search, ...dynamic, ...rest];
    const pillCandidates = ordered.filter((conf) => !isSearchOrDynamic(conf));

    return ordered.map((conf) => {
        if (isSearchOrDynamic(conf)) {
            return conf;
        }
        const pinned = shouldPinFilter(conf, pillCandidates);
        if (pinned) {
            return { ...conf, pinned: true };
        }
        const next = { ...conf };
        delete next.pinned;
        return next;
    });
}
